import apiClient from '../lib/axios';

export type OrderStatus = 'PENDING' | 'IN_PROGRESS' | 'READY_FOR_PICKUP' | 'COMPLETED' | 'CANCELLED';
export type FulfillmentType = 'PICKUP' | 'DELIVERY';

export interface PharmacyChainOrderListItem {
  id: string;
  trackingUUID: string;
  status: OrderStatus;
  fulfillmentType: FulfillmentType;
  patientName: string;
  patientPhone: string | null;
  doctor: { firstName: string; lastName: string; licenseNumber: string } | null;
  totalAmount: string;
  createdAt: string;
}

export interface PharmacyChainOrdersResponse {
  data: PharmacyChainOrderListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ListPharmacyChainOrdersQuery {
  status?: OrderStatus[];
  fulfillmentType?: FulfillmentType;
  search?: string;
  page?: number;
  limit?: number;
  completedWithinHours?: number;
  sort?: 'createdAt' | 'completedAt';
}

export async function listPharmacyChainOrders(
  query: ListPharmacyChainOrdersQuery = {},
): Promise<PharmacyChainOrdersResponse> {
  const params = new URLSearchParams();
  query.status?.forEach((s) => params.append('status', s));
  if (query.fulfillmentType) params.append('fulfillmentType', query.fulfillmentType);
  if (query.search) params.append('search', query.search);
  if (query.page) params.append('page', String(query.page));
  if (query.limit) params.append('limit', String(query.limit));
  if (query.completedWithinHours) {
    params.append('completedWithinHours', String(query.completedWithinHours));
  }
  if (query.sort) params.append('sort', query.sort);
  const { data } = await apiClient.get<PharmacyChainOrdersResponse>(
    `/pharmacy-chain/orders?${params.toString()}`,
  );
  return data;
}

export interface PharmacyChainOrderItem {
  id: string;
  pharmacyProduct: { name: string; barcode: string | null };
  quantity: number;
  price: string;
  insuranceCoverageAmount: string | null;
  finalPrice: string | null;
  instructions: string | null;
  durationDays: number | null;
}

export interface PharmacyChainOrderStatusLog {
  oldStatus: OrderStatus | null;
  newStatus: OrderStatus;
  createdAt: string;
}

export interface PharmacyChainOrderDetail {
  id: string;
  trackingUUID: string;
  status: OrderStatus;
  fulfillmentType: FulfillmentType;
  patientName: string;
  patientPhone: string | null;
  deliveryAddress: string | null;
  deliveryNotes: string | null;
  notes: string | null;
  pickupBranch: { id: string; name: string; address: string; phoneNumber: string | null } | null;
  totalAmount: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  doctor: { id: string; firstName: string; lastName: string; licenseNumber: string } | null;
  /** Patient-uploaded cédula / passport document (from the public tracking link). */
  nationalIdImageUrl: string | null;
  /** Patient-uploaded insurance card document (from the public tracking link). */
  insuranceIdImageUrl: string | null;
  user: { id: string; email: string; role: string };
  items: PharmacyChainOrderItem[];
  statusLogs: PharmacyChainOrderStatusLog[];
}

export async function getPharmacyChainOrder(id: string): Promise<PharmacyChainOrderDetail> {
  const { data } = await apiClient.get<PharmacyChainOrderDetail>(`/pharmacy-chain/orders/${id}`);
  return data;
}

export interface UpdateOrderStatusInput {
  status: OrderStatus;
  reason?: string;
}

export async function updatePharmacyChainOrderStatus(
  id: string,
  dto: UpdateOrderStatusInput,
): Promise<{ id: string; status: OrderStatus }> {
  const { data } = await apiClient.patch<{ id: string; status: OrderStatus }>(
    `/pharmacy-chain/orders/${id}/status`,
    dto,
  );
  return data;
}

export interface UpdateOrderItemInsuranceInput {
  insuranceCoverageAmount: string;
  finalPrice: string;
}

export async function updatePharmacyChainOrderItemInsurance(
  orderId: string,
  itemId: string,
  dto: UpdateOrderItemInsuranceInput,
): Promise<{ id: string; insuranceCoverageAmount: string; finalPrice: string }> {
  const { data } = await apiClient.patch<{ id: string; insuranceCoverageAmount: string; finalPrice: string }>(
    `/pharmacy-chain/orders/${orderId}/items/${itemId}/insurance`,
    dto,
  );
  return data;
}
